// src/pages/Dashboard/Dashboard.js

import React, { useState, useEffect } from "react";
import {
    Grid, Card, CardContent, Typography, Button, Paper,
    Box, Divider, IconButton, Chip, Avatar, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import GavelIcon from "@mui/icons-material/Gavel";
import ListIcon from "@mui/icons-material/List";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FeedbackIcon from "@mui/icons-material/Feedback";
import FeedbackBetaDialog from "../../components/FeedbackBetaDialog";
import PeopleIcon from "@mui/icons-material/People";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import CreateIcon from "@mui/icons-material/Create";

import { dashboardStatsJWT, bannersVisiblesJWT, eventosProximosAbogadoJWT } from "../../api/endpoints";
import CampaignIcon from "@mui/icons-material/Campaign";
import EventIcon from "@mui/icons-material/Event";
import GavelIconAlt from "@mui/icons-material/Gavel";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import GroupsIcon from "@mui/icons-material/Groups";

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        maxWidth: 1400,
        margin: "0 auto"
    },
    welcomeSection: {
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        color: theme.palette.primary.contrastText,
        padding: theme.spacing(4),
        borderRadius: 4,
        marginBottom: theme.spacing(3),
        boxShadow: `0 8px 32px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(23, 105, 170, 0.25)'}`
    },
    statCard: {
        height: "100%",
        borderRadius: 4,
        background: theme.palette.background.paper,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        transition: "all 0.3s ease",
        cursor: "pointer",
        "&:hover": {
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            transform: "translateY(-4px)"
        }
    },
    statIcon: {
        width: 60,
        height: 60,
        borderRadius: 4,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: theme.spacing(2),
        fontSize: 32
    },
    quickAccessCard: {
        borderRadius: 4,
        padding: theme.spacing(2.5),
        background: theme.palette.background.paper,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        height: "100%"
    },
    quickAccessButton: {
        width: "100%",
        justifyContent: "flex-start",
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        borderRadius: 4,
        textTransform: "none",
        transition: "all 0.2s ease",
        "&:hover": {
            background: theme.palette.action.hover,
            transform: "translateX(8px)"
        }
    },
    activityItem: {
        padding: theme.spacing(2),
        borderRadius: 4,
        marginBottom: theme.spacing(1.5),
        background: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.2s ease",
        cursor: "pointer",
        "&:hover": {
            background: theme.palette.action.hover,
            borderColor: theme.palette.primary.main
        }
    },
    moduleCard: {
        borderRadius: 4,
        padding: theme.spacing(2.5),
        background: theme.palette.background.paper,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        transition: "all 0.3s ease",
        cursor: "pointer",
        "&:hover": {
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            transform: "translateY(-4px)"
        }
    }
}));

function Dashboard({ usuario }) {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalExpedientes: 0,
        expedientesJudiciales: 0,
        expedientesAdministrativos: 0,
        misListas: 0
    });
    const [modulosDialogOpen, setModulosDialogOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    // banners: lista de banners visibles. bannerDetalleOpen: el que está abierto en modal.
    const [banners, setBanners] = useState([]);
    const [bannerDetalleOpen, setBannerDetalleOpen] = useState(null);
    const [bannersOcultos, setBannersOcultos] = useState(new Set()); // banners cerrados por el usuario en esta sesión

    useEffect(() => {
        cargarEstadisticas();
        bannersVisiblesJWT().then(r => {
            if (r?.ok) setBanners(r.items || []);
        }).catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cargarEstadisticas = async () => {
        setLoading(true);
        try {
            // Usar la nueva API con JWT
            const response = await dashboardStatsJWT();
            if (response?.ok) {
                setStats(response.stats || stats);
            }
        } catch (err) {
            console.error("Error al cargar estadísticas:", err);
            // Si falla el JWT (token expirado), redirigir a login
            if (err.message === "Token expirado") {
                localStorage.removeItem("SIGIF_JWT_TOKEN");
                window.location.href = "/login";
            }
        } finally {
            setLoading(false);
        }
    };

    const formatDate = () => {
        return new Date().toLocaleDateString('es-AR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatNombreApellido = (usuario) => {
        if (!usuario) return '';

        // Si tiene nombre y apellido separados, usar esos
        if (usuario.nombre && usuario.apellido) {
            const nombre = usuario.nombre.toLowerCase().split(' ').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
            const apellido = usuario.apellido.toLowerCase().split(' ').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
            return `${nombre} ${apellido}`;
        }

        // Si no, usar el campo completo
        if (usuario.nombre) {
            return usuario.nombre.toLowerCase().split(' ').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
        }

        return '';
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Buenos días";
        if (hour < 20) return "Buenas tardes";
        return "Buenas noches";
    };

    const modulos = usuario?.modulos || [];
    const hasExpedientes = modulos.some(m => m.clave === "expediente_judicial");
    const hasHonorarios = modulos.some(m => m.nombre?.toLowerCase().includes("honorarios"));
    const hasDocumentos = modulos.some(m => m.nombre?.toLowerCase().includes("documentos"));
    const hasPersonal = modulos.some(m => m.clave === "personal");
    const hasCargaMasiva = modulos.some(m => m.clave === "carga_masiva");

    // Para el dialog "Mis Módulos": un módulo puede aparecer varias veces si tiene
    // distintos accesos (expediente_judicial / carga_masiva / personal). Agrupamos
    // por id para que el usuario vea una sola tarjeta con un chip por cada acceso.
    // No tocamos `modulos` original — los .some() de arriba dependen de las entradas
    // separadas por clave.
    const modulosAgrupados = (() => {
        const map = new Map();
        for (const m of modulos) {
            const key = m.id;
            if (!map.has(key)) {
                map.set(key, { id: m.id, nombre: m.nombre, claves: [m.clave] });
            } else {
                const g = map.get(key);
                if (!g.claves.includes(m.clave)) g.claves.push(m.clave);
            }
        }
        return Array.from(map.values());
    })();

    // Determinar si es abogado (tiene función de abogado/fiscal/letrado/procurador).
    // Requiere id_personal real (>0); sin vinculación a Personal no puede tener expedientes.
    const tienePersonalReal = usuario?.id_personal && Number(usuario.id_personal) > 0;
    const esAbogado = tienePersonalReal && usuario?.funcion && (
        usuario.funcion.toLowerCase().includes("abogad") ||
        usuario.funcion.toLowerCase().includes("fiscal") ||
        usuario.funcion.toLowerCase().includes("letrad") ||
        usuario.funcion.toLowerCase().includes("procurad")
    );

    return (
        <div className={classes.root}>
            {/* Banners institucionales — render uno debajo del otro. Los que
                tienen Cuerpo_Detalle (HTML) son clickeables y abren un modal. */}
            {banners.filter(b => !bannersOcultos.has(b.Id)).map(banner => {
                const plain = (banner.Mensaje || '').replace(/<[^>]*>/g, '').trim() || banner.Titulo;
                const tieneDetalle = !!(banner.Cuerpo_Detalle && banner.Cuerpo_Detalle.trim());
                const bg = banner.Background_Color || '#1769aa';
                return (
                    <Paper key={banner.Id} sx={{
                        mb: 1.5, borderRadius: '4px', overflow: 'hidden',
                        background: `linear-gradient(135deg, ${bg} 0%, #2196f3 100%)`,
                        color: '#fff', position: 'relative',
                        display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.5,
                        cursor: tieneDetalle ? 'pointer' : 'default',
                        transition: 'transform 0.15s',
                        '&:hover': tieneDetalle ? { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' } : {},
                    }}
                        onClick={() => { if (tieneDetalle) setBannerDetalleOpen(banner); }}>
                        <CampaignIcon sx={{ fontSize: 24, opacity: 0.85 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {plain}
                            </Typography>
                            {tieneDetalle && (
                                <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mt: 0.3 }}>
                                    Click para ver más ▸
                                </Typography>
                            )}
                        </Box>
                        <IconButton size="small"
                            onClick={(e) => { e.stopPropagation(); setBannersOcultos(prev => new Set(prev).add(banner.Id)); }}
                            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Paper>
                );
            })}

            {/* Modal con el cuerpo HTML del banner clickeado */}
            <Dialog open={!!bannerDetalleOpen} onClose={() => setBannerDetalleOpen(null)}
                maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
                <DialogTitle sx={{
                    background: `linear-gradient(135deg, ${bannerDetalleOpen?.Background_Color || '#1769aa'} 0%, #2196f3 100%)`,
                    color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5,
                }}>
                    <CampaignIcon />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ display: 'block', opacity: 0.85, fontWeight: 700, textTransform: 'uppercase' }}>
                            {bannerDetalleOpen?.Titulo || 'SIGIF'}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            {(bannerDetalleOpen?.Mensaje || '').replace(/<[^>]*>/g, '').trim()}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setBannerDetalleOpen(null)} sx={{ color: '#fff' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    {/* El HTML viene del admin — confiamos el contenido (solo lo edita admin autorizado). */}
                    <Box dangerouslySetInnerHTML={{ __html: bannerDetalleOpen?.Cuerpo_Detalle || '' }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBannerDetalleOpen(null)} variant="contained" sx={{ bgcolor: '#1769aa', textTransform: 'none' }}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Sección de bienvenida */}
            <Paper className={classes.welcomeSection} elevation={0}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                    <Box>
                        <Typography variant="h4" style={{ fontWeight: 700, marginBottom: 8 }}>
                            {getGreeting()}, {formatNombreApellido(usuario)}
                        </Typography>
                        <Typography variant="body1" style={{ opacity: 0.9 }}>
                            {formatDate()}
                        </Typography>
                    </Box>
                    {/* Mostrar botón actualizar solo para abogados */}
                    {esAbogado && (
                        <Box display="flex" gap={1}>
                            <IconButton
                                onClick={cargarEstadisticas}
                                style={{ background: "rgba(255,255,255,0.2)", color: "#fff" }}
                                size="large">
                                <RefreshIcon />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Paper>
            {/* Estadísticas rápidas — cards condicionales por perfil del user.
                Las que tienen valor 0 se muestran en gris atenuado para no distraer. */}
            <DashboardCards stats={stats} loading={loading} navigate={navigate} usuario={usuario}
                hasPersonal={hasPersonal} hasCargaMasiva={hasCargaMasiva} esAbogado={esAbogado} permisos={null} />

            {/* Próximos eventos (audiencias / vencimientos / reuniones) — solo abogados */}
            {esAbogado && (
                <ProximosEventosPanel navigate={navigate} />
            )}

            <Grid container spacing={3}>
                {/* Accesos rápidos */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper className={classes.quickAccessCard}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <TrendingUpIcon style={{ color: theme.palette.primary.main, marginRight: 8 }} />
                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                Accesos Rápidos
                            </Typography>
                        </Box>
                        <Divider style={{ marginBottom: 16 }} />

                        {hasExpedientes && (
                            <>
                                <Button
                                    className={classes.quickAccessButton}
                                    startIcon={<GavelIcon />}
                                    endIcon={<ArrowForwardIcon fontSize="small" />}
                                    onClick={() => navigate("/expedientes?tab=0")}
                                >
                                    Expedientes Judiciales
                                </Button>
                                <Button
                                    className={classes.quickAccessButton}
                                    startIcon={<DescriptionIcon />}
                                    endIcon={<ArrowForwardIcon fontSize="small" />}
                                    onClick={() => navigate("/expedientes?tab=1")}
                                >
                                    Expedientes Administrativos
                                </Button>
                                <Button
                                    className={classes.quickAccessButton}
                                    startIcon={<ListIcon />}
                                    endIcon={<ArrowForwardIcon fontSize="small" />}
                                    onClick={() => navigate("/listas-trabajo/mis-listas")}
                                >
                                    Mis Listas de Trabajo
                                </Button>
                            </>
                        )}

                        {hasHonorarios && (
                            <Button
                                className={classes.quickAccessButton}
                                startIcon={<AccountBalanceIcon />}
                                endIcon={<ArrowForwardIcon fontSize="small" />}
                                onClick={() => navigate("/honorarios")}
                            >
                                Honorarios
                            </Button>
                        )}

                        {hasDocumentos && (
                            <Button
                                className={classes.quickAccessButton}
                                startIcon={<AssignmentIcon />}
                                endIcon={<ArrowForwardIcon fontSize="small" />}
                                onClick={() => navigate("/documentos")}
                            >
                                Documentos
                            </Button>
                        )}

                        {hasPersonal && (
                            <Button
                                className={classes.quickAccessButton}
                                startIcon={<PeopleIcon />}
                                endIcon={<ArrowForwardIcon fontSize="small" />}
                                onClick={() => navigate("/personal")}
                            >
                                Personal
                            </Button>
                        )}
                    </Paper>
                </Grid>

                {/* Información y recursos */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper className={classes.quickAccessCard}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <AssignmentIcon style={{ color: theme.palette.primary.main, marginRight: 8 }} />
                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                Recursos y Herramientas
                            </Typography>
                        </Box>
                        <Divider style={{ marginBottom: 16 }} />

                        <Grid container spacing={2}>
                            {/* Información del usuario */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box className={classes.activityItem}>
                                    <Box display="flex" alignItems="center">
                                        <Avatar
                                            style={{
                                                background: "#e3f2fd",
                                                color: theme.palette.primary.main,
                                                marginRight: 12,
                                                width: 48,
                                                height: 48
                                            }}
                                        >
                                            <AccountCircleIcon />
                                        </Avatar>
                                        <Box flex={1}>
                                            <Typography variant="body2" color="text.secondary" style={{ fontSize: 11 }}>
                                                Tu función
                                            </Typography>
                                            <Typography variant="body1" style={{ fontWeight: 600 }}>
                                                {usuario?.funcion || "No especificada"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Módulos disponibles - clickeable */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box
                                    className={classes.activityItem}
                                    onClick={() => setModulosDialogOpen(true)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <Box display="flex" alignItems="center">
                                        <Avatar
                                            style={{
                                                background: "#e8f5e9",
                                                color: "#2e7d32",
                                                marginRight: 12,
                                                width: 48,
                                                height: 48
                                            }}
                                        >
                                            <FolderIcon />
                                        </Avatar>
                                        <Box flex={1}>
                                            <Typography variant="body2" color="text.secondary" style={{ fontSize: 11 }}>
                                                Módulos asignados
                                            </Typography>
                                            <Typography variant="h5" style={{ fontWeight: 700, color: "#2e7d32" }}>
                                                {modulos.length}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: "#666" }}>
                                                {modulos.length === 1 ? "módulo activo" : "módulos activos"} • Click para ver
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Acceso a Listas */}
                            {hasExpedientes && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Box
                                        className={classes.activityItem}
                                        onClick={() => navigate("/listas-trabajo/mis-listas")}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <Box display="flex" alignItems="center">
                                            <Avatar
                                                style={{
                                                    background: "#fff3e0",
                                                    color: "#f57c00",
                                                    marginRight: 12,
                                                    width: 48,
                                                    height: 48
                                                }}
                                            >
                                                <ListIcon />
                                            </Avatar>
                                            <Box flex={1}>
                                                <Typography variant="body2" color="text.secondary" style={{ fontSize: 11 }}>
                                                    Mis listas de trabajo
                                                </Typography>
                                                <Typography variant="h5" style={{ fontWeight: 700, color: "#f57c00" }}>
                                                    {stats.misListas || 0}
                                                </Typography>
                                                <Typography variant="caption" style={{ color: "#666" }}>
                                                    {stats.misListas === 1 ? "lista creada" : "listas creadas"}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            )}

                            {/* Mis Escritos */}
                            {hasExpedientes && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Box
                                        className={classes.activityItem}
                                        onClick={() => navigate("/documentos?tab=mios")}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <Box display="flex" alignItems="center">
                                            <Avatar
                                                style={{
                                                    background: "#fce4ec",
                                                    color: "#c2185b",
                                                    marginRight: 12,
                                                    width: 48,
                                                    height: 48
                                                }}
                                            >
                                                <CreateIcon />
                                            </Avatar>
                                            <Box flex={1}>
                                                <Typography variant="body2" color="text.secondary" style={{ fontSize: 11 }}>
                                                    Escritos
                                                </Typography>
                                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                                    Mis Escritos
                                                </Typography>
                                                <Typography variant="caption" style={{ color: "#666" }}>
                                                    Ver y gestionar escritos
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            )}

                            {/* Documentos */}
                            {hasDocumentos && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Box
                                        className={classes.activityItem}
                                        onClick={() => navigate("/documentos")}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <Box display="flex" alignItems="center">
                                            <Avatar
                                                style={{
                                                    background: "#f3e5f5",
                                                    color: "#7b1fa2",
                                                    marginRight: 12,
                                                    width: 48,
                                                    height: 48
                                                }}
                                            >
                                                <DescriptionIcon />
                                            </Avatar>
                                            <Box flex={1}>
                                                <Typography variant="body2" color="text.secondary" style={{ fontSize: 11 }}>
                                                    Biblioteca
                                                </Typography>
                                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                                    Documentos
                                                </Typography>
                                                <Typography variant="caption" style={{ color: "#666" }}>
                                                    Acceder a documentos
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
            {/* Accesos rápidos adicionales */}
            <Grid container spacing={3} style={{ marginTop: 8 }}>
                {/* Atajos de teclado / Tips */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper style={{ padding: 24, borderRadius: 4, background: "#fff", height: "100%" }}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <AssignmentIcon style={{ color: theme.palette.primary.main, marginRight: 8 }} />
                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                Tips y Atajos
                            </Typography>
                        </Box>
                        <Divider style={{ marginBottom: 16 }} />
                        <Box>
                            <Box mb={2}>
                                <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                                    <span role="img" aria-label="notificaciones">🔔</span> Notificaciones
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Hacé clic en la campana del header para ver tus notificaciones y alertas
                                </Typography>
                            </Box>
                            <Box mb={2}>
                                <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                                    <span role="img" aria-label="listas">📋</span> Listas de Trabajo
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Organizá tus expedientes en listas personalizadas para un acceso más rápido
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                                    <span role="img" aria-label="búsqueda">🔍</span> Búsqueda Rápida
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Usá los filtros avanzados en cada sección para encontrar información específica
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Reportar comentarios / problemas */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper style={{ padding: 24, borderRadius: 4, background: "#f8fafc", height: "100%" }}>
                        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 12 }}>
                            ¿Encontraste algo para mejorar?
                        </Typography>
                        <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
                            Contanos cualquier error, sugerencia o duda. Podés adjuntar una captura de pantalla. Tu reporte llega directo al equipo de SIGIF.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<FeedbackIcon />}
                            onClick={() => setFeedbackOpen(true)}
                            fullWidth
                        >
                            Reportar comentario
                        </Button>
                    </Paper>
                </Grid>
            </Grid>
            {/* Dialog de feedback (reportar comentario) */}
            <FeedbackBetaDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
            {/* Modal de Módulos */}
            <Dialog
                open={modulosDialogOpen}
                onClose={() => setModulosDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            Mis Módulos Asignados
                        </Typography>
                        <IconButton onClick={() => setModulosDialogOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {modulosAgrupados.length === 0 ? (
                        <Box py={4} textAlign="center">
                            <FolderIcon style={{ fontSize: 64, color: "#ccc", marginBottom: 16 }} />
                            <Typography variant="body1" color="text.secondary">
                                No tenés módulos asignados
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Contactá al administrador del sistema
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {modulosAgrupados.map((modulo) => {
                                // Avatar según el acceso "principal" (expediente_judicial > personal > otro)
                                const claveIcon = modulo.claves.includes("expediente_judicial")
                                    ? "expediente_judicial"
                                    : modulo.claves.includes("personal")
                                        ? "personal"
                                        : modulo.claves[0];
                                return (
                                    <Grid size={12} key={modulo.id}>
                                        <Box
                                            style={{
                                                padding: 16,
                                                borderRadius: 4,
                                                background: "#f8fafc",
                                                border: "1px solid #e2e8f0"
                                            }}
                                        >
                                            <Box display="flex" alignItems="center">
                                                <Avatar
                                                    style={{
                                                        background: "#e3f2fd",
                                                        color: theme.palette.primary.main,
                                                        marginRight: 12,
                                                        width: 48,
                                                        height: 48
                                                    }}
                                                >
                                                    {claveIcon === "expediente_judicial" ? (
                                                        <GavelIcon />
                                                    ) : claveIcon === "personal" ? (
                                                        <PeopleIcon />
                                                    ) : (
                                                        <FolderIcon />
                                                    )}
                                                </Avatar>
                                                <Box flex={1}>
                                                    <Typography variant="body1" style={{ fontWeight: 600, marginBottom: 4 }}>
                                                        {modulo.nombre}
                                                    </Typography>
                                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                                        {modulo.claves.map((clv) => (
                                                            <Chip
                                                                key={clv}
                                                                label={clv}
                                                                size="small"
                                                                style={{
                                                                    background: "#e3f2fd",
                                                                    color: theme.palette.primary.main,
                                                                    fontSize: 11,
                                                                    height: 24
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModulosDialogOpen(false)} color="primary">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// ProximosEventosPanel: lista compacta de los eventos del abogado en los
// próximos 7 días. Pega contra /api/eventos/proximos-abogado.
// ─────────────────────────────────────────────────────────────────────────
function ProximosEventosPanel({ navigate }) {
    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancel = false;
        eventosProximosAbogadoJWT().then(r => {
            if (cancel) return;
            if (r?.ok) setEventos(r.items || []);
        }).catch(() => { }).finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, []);

    if (loading) return null;
    if (eventos.length === 0) return null;

    const iconoTipo = (tipo) => {
        if (tipo === 'audiencia') return <GavelIconAlt fontSize="small" />;
        if (tipo === 'vencimiento') return <EventBusyIcon fontSize="small" />;
        if (tipo === 'reunion') return <GroupsIcon fontSize="small" />;
        return <EventIcon fontSize="small" />;
    };
    const colorTipo = (tipo) => {
        if (tipo === 'audiencia') return '#6a1b9a';
        if (tipo === 'vencimiento') return '#c62828';
        if (tipo === 'reunion') return '#1565c0';
        return '#546e7a';
    };
    const fmtFecha = (s) => {
        try {
            return new Date(s).toLocaleString('es-AR', {
                day: '2-digit', month: '2-digit',
                hour: '2-digit', minute: '2-digit', hour12: false,
            });
        } catch { return ''; }
    };

    return (
        <Paper sx={{ p: 2, mb: 3, borderRadius: '4px', borderLeft: '4px solid #f9a825' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <EventIcon sx={{ color: '#f9a825', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    Próximos eventos
                </Typography>
                <Chip label={`${eventos.length} en 7 días`} size="small" sx={{ bgcolor: '#fff8e1', color: '#e65100', fontWeight: 600 }} />
            </Box>
            <Divider sx={{ mb: 1 }} />
            <Box>
                {eventos.slice(0, 5).map(ev => (
                    <Box key={ev.Id} sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, py: 0.8,
                        borderBottom: '1px dashed #eee', cursor: 'pointer',
                        '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                        onClick={() => window.open(`/expediente/judicial/${ev.Id_Expediente}`, '_blank', 'noopener,noreferrer')}>
                        <Box sx={{ color: colorTipo(ev.Tipo) }}>{iconoTipo(ev.Tipo)}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {ev.Titulo}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                {ev.Autos || ''} · {(ev.Caratula || '').slice(0, 60)}{(ev.Caratula || '').length > 60 ? '…' : ''}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: colorTipo(ev.Tipo) }}>
                                {fmtFecha(ev.Fecha_Evento)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#888' }}>
                                {ev.Dias_Hasta === 0 ? 'HOY' : ev.Dias_Hasta === 1 ? 'mañana' : `en ${ev.Dias_Hasta} días`}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
            {eventos.length > 5 && (
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: '#666' }}>
                    + {eventos.length - 5} eventos más en los próximos 7 días
                </Typography>
            )}
        </Paper>
    );
}

// ─────────────────────────────────────────────────────────────────────────
// DashboardCards: render dinámico de cards según perfil/permisos del user.
// La info viene del endpoint /api/dashboard/stats (1 query multi-resultset
// con cache de 2 min en backend) — no genera más carga que la de antes,
// independientemente del rol.
// ─────────────────────────────────────────────────────────────────────────
function DashboardCards({ stats, loading, navigate, usuario, hasPersonal, hasCargaMasiva, esAbogado }) {
    const cards = [];

    // Comunes (todos)
    const c = stats.comunes || {};
    cards.push({ label: 'Mis Listas', value: c.misListas || 0, icon: '📋', bg: '#e8f5e9', color: '#2e7d32', go: '/listas-trabajo/mis-listas' });
    // Notificaciones: el panel vive en el Header como Popover, no hay ruta.
    // Disparamos evento custom que escucha el Header y abre el panel.
    cards.push({ label: 'Notificaciones sin leer', value: c.notificacionesSinLeer || 0, icon: '🔔', bg: '#fff8e1', color: '#f57f17', onAction: () => window.dispatchEvent(new CustomEvent('sigif-open-notificaciones')) });
    // Pases recibidos: no hay ruta /pases (el panel vive en el Header como Drawer).
    // Disparamos evento custom que escucha el Header y abre la bandeja de entrada.
    cards.push({ label: 'Pases recibidos', value: c.pasesRecibidos || 0, icon: '📥', bg: '#e0f7fa', color: '#00838f', onAction: () => window.dispatchEvent(new CustomEvent('sigif-open-bandeja')) });

    // Apoderado: si tiene función de abogado o tiene firmas/expedientes
    const a = stats.apoderado || {};
    if (esAbogado || a.expedientesComoApoderado > 0 || a.demandasParaFirmar > 0) {
        if (a.demandasParaFirmar > 0) {
            cards.push({ label: 'Demandas para firmar', value: a.demandasParaFirmar, icon: '✍️', bg: '#ffebee', color: '#c62828', go: '/panel-firma-abogado', highlight: true });
        }
        if (a.documentosEnCirculacion > 0) {
            cards.push({ label: 'Documentos en circulación', value: a.documentosEnCirculacion, icon: '📜', bg: '#fff3e0', color: '#e65100', go: '/panel-firma-abogado' });
        }
        cards.push({ label: 'Expedientes como apoderado', value: a.expedientesComoApoderado || 0, icon: '⚖️', bg: '#f3e5f5', color: '#6a1b9a', go: '/expedientes' });
        if (a.honorariosPendientes > 0) {
            cards.push({ label: 'Certificados para honorarios', value: a.honorariosPendientes, icon: '💰', bg: '#e8f5e9', color: '#2e7d32', go: '/honorarios' });
        }
    }

    // Autoridad (Fiscal/Director): mostrar globales si la función matchea
    const aut = stats.autoridad || {};
    const funcion = (usuario?.funcion || '').toLowerCase();
    const esAutoridad = funcion.includes('fiscal de estado') || funcion.includes('director');
    if (esAutoridad) {
        cards.push({ label: 'Total Expedientes', value: aut.totalExpedientes || 0, icon: '📂', bg: '#e3f2fd', color: '#1565c0', go: '/expedientes' });
        cards.push({ label: 'Judiciales', value: aut.judiciales || 0, icon: '⚖️', bg: '#f3e5f5', color: '#6a1b9a', go: '/expedientes?tab=0' });
        cards.push({ label: 'Administrativos', value: aut.administrativos || 0, icon: '📄', bg: '#fff3e0', color: '#e65100', go: '/expedientes?tab=1' });
        if (aut.docsPendientesFirma > 0) {
            cards.push({ label: 'Docs para mi firma', value: aut.docsPendientesFirma, icon: '🖋️', bg: '#ffebee', color: '#c62828', go: '/panel-firma', highlight: true });
        }
    }

    // Carga Masiva: solo a usuarios con permiso del módulo. Las cards mostraban
    // las stats globales del sistema y aparecían a TODOS los usuarios (no servía
    // para abogados ni gente que no toca CM) — ruido innecesario en el dashboard.
    const cm = stats.cargaMasiva || {};
    if (hasCargaMasiva && (cm.lotesActivos > 0 || cm.lotesPendientesPJ > 0)) {
        cards.push({ label: 'Lotes activos', value: cm.lotesActivos || 0, icon: '📦', bg: '#ede7f6', color: '#4527a0', go: '/gestion-lotes' });
        if (cm.lotesConErrores > 0) {
            cards.push({ label: 'Lotes con errores', value: cm.lotesConErrores, icon: '⚠️', bg: '#ffebee', color: '#c62828', go: '/gestion-lotes', highlight: true });
        }
        if (cm.lotesPendientesPJ > 0) {
            cards.push({ label: 'Lotes pendientes PJ', value: cm.lotesPendientesPJ, icon: '📤', bg: '#fff8e1', color: '#f57f17', go: '/gestion-lotes' });
        }
    }

    // Personal/RRHH (solo si tiene permiso de Personal)
    const p = stats.personal || {};
    if (hasPersonal && (p.licenciasPendientes > 0 || p.vacacionesPorVencer > 0)) {
        if (p.licenciasPendientes > 0) {
            cards.push({ label: 'Licencias pendientes', value: p.licenciasPendientes, icon: '🏖️', bg: '#e1f5fe', color: '#01579b', go: '/personal' });
        }
        if (p.vacacionesPorVencer > 0) {
            cards.push({ label: 'Vacaciones por vencer (30 días)', value: p.vacacionesPorVencer, icon: '⏰', bg: '#fff3e0', color: '#e65100', go: '/personal' });
        }
    }

    if (cards.length === 0 && !loading) return null;

    return (
        <Grid container spacing={1.5} style={{ marginBottom: 20 }}>
            {cards.map((card, i) => {
                const atenuado = card.value === 0;
                const clickeable = !!(card.go || card.onAction);
                return (
                    <Grid key={i} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                        <Card
                            sx={{
                                height: '100%', borderRadius: '4px',
                                cursor: clickeable ? 'pointer' : 'default',
                                opacity: atenuado ? 0.55 : 1,
                                border: card.highlight ? `2px solid ${card.color}` : '1px solid rgba(0,0,0,0.06)',
                                transition: 'all 0.2s',
                                '&:hover': clickeable ? { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } : {}
                            }}
                            onClick={() => {
                                if (card.onAction) card.onAction();
                                else if (card.go) navigate(card.go);
                            }}
                        >
                            <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Box sx={{
                                        width: 32, height: 32, borderRadius: '4px',
                                        background: card.bg, display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                        fontSize: 16, flexShrink: 0,
                                    }}>
                                        {card.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: card.color, fontSize: '1.4rem', lineHeight: 1 }}>
                                        {loading ? <CircularProgress size={18} /> : card.value}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.72rem', lineHeight: 1.2 }}>
                                    {card.label}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
}

export default Dashboard;
